import { defineStore } from 'pinia'
import { injToken } from '@shared/data/token'
import { sharedGetToken } from '@shared/utils/helper'
import { INJ_REQUIRED_FOR_GAS } from '@shared/utils/constant'
import { toUiCw20Balance } from '@shared/transformer/explorer'
import { getDefaultSubaccountId } from '@injectivelabs/sdk-ts'
import {
  injNameService,
  indexerDerivativesApi,
  injBonfidaNameService,
  indexerRestExplorerApi,
  indexerAccountPortfolioApi
} from '@shared/Service'
import { AccountNotFoundException } from '@/app/exceptions/AccountNotFoundException'
import { AccountFormatInvalidException } from '@/app/exceptions/AccountFormatInvalidException'
import {
  getSubaccountBalancesMap,
  appendPositionsToAccountBalancesMap
} from '@/app/client/utils/account'
import type { AccountBalance } from '@/types'
import type { PositionV2 } from '@injectivelabs/sdk-ts'
import type { SharedBalanceWithToken } from '@shared/types'

type AccountStoreState = {
  positions: PositionV2[]
  cw20Balances: SharedBalanceWithToken[]
  accountBalancesMap: Record<
    string /** string = subaccountId */,
    AccountBalance[]
  >
  balanceDenoms: string[]
}

const initialStateFactory = (): AccountStoreState => ({
  positions: [],
  cw20Balances: [],
  balanceDenoms: [] /** all denoms that the account has in their balance */,
  accountBalancesMap: {}
})

export const useAccountStore = defineStore('account', {
  state: (): AccountStoreState => initialStateFactory(),
  getters: {
    hasEnoughInjForGas: (state: AccountStoreState) => {
      const sharedWalletStore = useSharedWalletStore()

      const bankAccount =
        state.accountBalancesMap[
          getDefaultSubaccountId(sharedWalletStore.injectiveAddress)
        ]

      if (!bankAccount) {
        return false
      }

      const injBankBalance = bankAccount.find(
        ({ denom }) => denom === injToken.denom
      )?.bankBalance

      if (!injBankBalance) {
        return false
      }

      return sharedToBalanceInTokenInBase({
        value: injBankBalance
      }).gte(INJ_REQUIRED_FOR_GAS)
    }
  },
  actions: {
    reset() {
      useAccountStore().$reset()
    },

    fetchInjAddressFromNameService(name: string): Promise<string> {
      return injNameService.fetchInjAddress(name.trim().toLowerCase())
    },

    fetchInjAddressFromBonfidaNameService(name: string): Promise<string> {
      return injBonfidaNameService.fetchInjAddress(name.trim().toLowerCase())
    },

    async fetchPositions(injectiveAddress: string) {
      const accountStore = useAccountStore()

      if (accountStore.positions.length > 0) {
        return
      }

      const { positions } = await indexerDerivativesApi.fetchPositionsV2({
        address: injectiveAddress,
        pagination: {
          limit: 100
        }
      })

      const accountBalancesMap = appendPositionsToAccountBalancesMap(
        accountStore.accountBalancesMap,
        positions
      )

      accountStore.$patch({
        positions,
        accountBalancesMap
      })
    },

    async fetchCW20BalancesNoThrow(address: string) {
      const accountStore = useAccountStore()

      const cw20Balances =
        await indexerRestExplorerApi.fetchCW20BalancesNoThrow(address)

      const formattedCW20Balances = cw20Balances.map(toUiCw20Balance)

      const cw20BalanceWithToken = await Promise.all(
        formattedCW20Balances.map(async (balance) => {
          const token = await sharedGetToken(balance.denom)

          return {
            ...balance,
            token: token || balance.token
          }
        })
      )

      accountStore.$patch({
        cw20Balances: cw20BalanceWithToken
      })
    },

    async fetchPortfolio(injectiveAddress: string) {
      const appStore = useAppStore()
      const sharedTokenStore = useSharedTokenStore()
      const accountStore = useAccountStore()

      try {
        const response =
          await indexerAccountPortfolioApi.fetchAccountPortfolioBalances(
            injectiveAddress
          )

        if (!response) {
          appStore.$patch({
            searchParam: injectiveAddress
          })

          throw new AccountNotFoundException()
        }

        const { balanceDenoms, accountBalancesMap } = getSubaccountBalancesMap({
          ...response,
          positionsWithUpnlList: []
        })

        await sharedTokenStore.fetchTokensUsdPriceMap()

        accountStore.$patch({
          balanceDenoms,
          accountBalancesMap
        })
      } catch (e: any) {
        if (e.message.includes(404)) {
          appStore.$patch({
            searchParam: injectiveAddress
          })

          throw new AccountNotFoundException()
        }

        if (e.message.includes('decoding bech32 failed')) {
          appStore.$patch({
            searchParam: injectiveAddress
          })

          throw new AccountFormatInvalidException()
        }

        throw e
      }
    }
  }
})
